import React, { useState, useEffect } from "react";
import { Plus, Star, Search, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ChatNewProjectModal } from "./ChatNewProjectModal";
import { formatDistanceToNow } from "date-fns";

interface ChatProject {
  id: string;
  name: string;
  description: string | null;
  is_starred: boolean;
  created_at: string;
  updated_at: string;
}

interface ChatProjectsPageProps {
  onSelectProject: (projectId: string) => void;
}

export const ChatProjectsPage: React.FC<ChatProjectsPageProps> = ({ onSelectProject }) => {
  const [projects, setProjects] = useState<ChatProject[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_projects")
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const toggleStar = async (e: React.MouseEvent, projectId: string, currentState: boolean) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from("chat_projects")
        .update({ is_starred: !currentState })
        .eq("id", projectId);

      if (error) throw error;
      setProjects(projects.map(p => 
        p.id === projectId ? { ...p, is_starred: !currentState } : p
      ));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteProject = async (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from("chat_projects")
        .delete()
        .eq("id", projectId);

      if (error) throw error;
      setProjects(projects.filter(p => p.id !== projectId));
      toast({
        title: "Project deleted",
        description: "The project has been deleted.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleProjectCreated = (projectId: string) => {
    fetchProjects();
    onSelectProject(projectId);
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const starredProjects = filteredProjects.filter(p => p.is_starred);
  const otherProjects = filteredProjects.filter(p => !p.is_starred);

  return (
    <div className="flex flex-col h-full p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Projects</h1>
        <Button onClick={() => setIsModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New project
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Projects Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground">
          <p className="text-lg mb-4">No projects yet</p>
          <Button onClick={() => setIsModalOpen(true)} variant="outline">
            Create your first project
          </Button>
        </div>
      ) : (
        <div className="space-y-6 overflow-auto flex-1">
          {starredProjects.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Starred</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {starredProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={() => onSelectProject(project.id)}
                    onToggleStar={(e) => toggleStar(e, project.id, project.is_starred)}
                    onDelete={(e) => deleteProject(e, project.id)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {otherProjects.length > 0 && (
            <div>
              {starredProjects.length > 0 && (
                <h2 className="text-sm font-medium text-muted-foreground mb-3">All projects</h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {otherProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onSelect={() => onSelectProject(project.id)}
                    onToggleStar={(e) => toggleStar(e, project.id, project.is_starred)}
                    onDelete={(e) => deleteProject(e, project.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ChatNewProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
};

interface ProjectCardProps {
  project: ChatProject;
  onSelect: () => void;
  onToggleStar: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onSelect, onToggleStar, onDelete }) => {
  return (
    <Card
      className="cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground truncate flex-1">{project.name}</h3>
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleStar}
            >
              <Star
                className={`h-4 w-4 ${project.is_starred ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
              />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {project.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{project.description}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Updated {formatDistanceToNow(new Date(project.updated_at), { addSuffix: true })}
        </p>
      </CardContent>
    </Card>
  );
};
